import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Map, X } from 'lucide-react';
import { createActivity, fetchRoutes } from '../../api/move';
import type { MoveType, MoveRouteSummary } from '../../api/move';
import styles from '../../styles/Move.module.css';

const TYPES: { key: MoveType; label: string }[] = [
  { key: 'walk', label: 'Caminar' },
  { key: 'run', label: 'Correr' },
  { key: 'bike', label: 'Bici' },
  { key: 'mixed', label: 'Mixto' },
];

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

export default function ActivityRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tipo, setTipo] = useState<MoveType>('run');
  const [distanciaKm, setDistanciaKm] = useState('');
  const [duracion, setDuracion] = useState('');
  const [desnivel, setDesnivel] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 16));
  const [notas, setNotas] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Route linking
  const [routes, setRoutes] = useState<MoveRouteSummary[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<MoveRouteSummary | null>(null);
  const [showRoutePicker, setShowRoutePicker] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');

  useEffect(() => {
    const routeId = Number(new URLSearchParams(location.search).get('route_id') || 0);
    fetchRoutes()
      .then(r => {
        setRoutes(r);
        if (!routeId) return;
        const route = r.find(item => Number(item.route_id) === routeId);
        if (!route) return;
        setSelectedRoute(route);
        setTipo(route.tipo);
        if (!distanciaKm) setDistanciaKm((Number(route.distancia_m || 0) / 1000).toFixed(1));
        if (!duracion) setDuracion(String(Number(route.duracion_min || 0) || ''));
        if (!desnivel) setDesnivel(String(Number(route.desnivel_m || 0) || ''));
      })
      .catch(() => {});
  }, [location.search]);

  const filteredRoutes = routes.filter(r =>
    r.titulo.toLowerCase().includes(routeSearch.toLowerCase())
  );

  async function handleSubmit() {
    setError(null);
    const distM = Math.round(parseFloat(distanciaKm) * 1000) || 0;
    const durMin = parseInt(duracion, 10) || 0;
    if (distM <= 0 || durMin <= 0) {
      setError('Introduce distancia y duración');
      return;
    }
    setBusy(true);
    try {
      await createActivity({
        tipo,
        distancia_m: distM,
        duracion_min: durMin,
        desnivel_m: parseInt(desnivel, 10) || 0,
        fecha: fecha.replace('T', ' ') + ':00',
        notas: notas.trim() || null,
        route_id: selectedRoute?.route_id ?? null,
      });
      navigate('/move/activity', { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo registrar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <p className={styles.greetingLabel}>Hábitos</p>
          <h1 className={styles.greetingName}>Registrar actividad</h1>
        </div>
      </header>

      <section className={styles.section}>
        <div className={styles.card}>
          <label className={styles.label}>Tipo de actividad</label>
          <div className={styles.pillRow} style={{ padding: 0, margin: '8px 0 16px' }}>
            {TYPES.map(t => (
              <button
                key={t.key}
                className={`${styles.pill} ${tipo === t.key ? styles.pillActive : ''}`}
                onClick={() => setTipo(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className={styles.label}>Distancia (km)</label>
              <input
                className={styles.input}
                type="number" step="0.1" inputMode="decimal"
                value={distanciaKm}
                onChange={e => setDistanciaKm(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Duración (min)</label>
              <input
                className={styles.input}
                type="number" inputMode="numeric"
                value={duracion}
                onChange={e => setDuracion(e.target.value)}
              />
            </div>
          </div>

          <label className={styles.label} style={{ marginTop: 12 }}>Desnivel (m)</label>
          <input
            className={styles.input}
            type="number" inputMode="numeric"
            value={desnivel}
            onChange={e => setDesnivel(e.target.value)}
          />

          <label className={styles.label} style={{ marginTop: 12 }}>Fecha</label>
          <input
            className={styles.input}
            type="datetime-local"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
          />

          {/* Route linker */}
          <label className={styles.label} style={{ marginTop: 12 }}>Ruta (opcional)</label>
          {selectedRoute ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'hsl(142 76% 45% / 0.08)', border: '1px solid hsl(142 76% 45% / 0.25)',
              borderRadius: 12, padding: '10px 12px',
            }}>
              <Map size={16} color="var(--move-primary)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--move-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedRoute.titulo}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--move-muted-fg)' }}>
                  {formatDistance(selectedRoute.distancia_m)} · +{selectedRoute.desnivel_m} m
                </p>
              </div>
              <button
                onClick={() => setSelectedRoute(null)}
                style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--move-muted-fg)', padding: 4, display: 'flex' }}
                aria-label="Quitar ruta"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              className={styles.btnGhost}
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              onClick={() => setShowRoutePicker(true)}
            >
              <Map size={15} /> Vincular una ruta
            </button>
          )}

          <label className={styles.label} style={{ marginTop: 12 }}>Notas</label>
          <textarea
            className={styles.input}
            value={notas}
            onChange={e => setNotas(e.target.value)}
            rows={3}
          />

          {error && <p style={{ color: 'hsl(348 90% 50%)', marginTop: 12, fontSize: 13 }}>{error}</p>}

          <button
            disabled={busy}
            onClick={handleSubmit}
            className={styles.btnPrimary}
            style={{ width: '100%', marginTop: 16 }}
          >
            {busy ? 'Guardando…' : 'Guardar actividad'}
          </button>
        </div>
      </section>

      {/* Route picker sheet */}
      {showRoutePicker && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 200, display: 'flex', alignItems: 'flex-end',
          }}
          onClick={() => setShowRoutePicker(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '20px 20px 0 0',
              width: '100%', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
              padding: '0 0 env(safe-area-inset-bottom)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 16, color: 'var(--move-fg)' }}>Seleccionar ruta</p>
              <input
                className={styles.input}
                placeholder="Buscar ruta…"
                value={routeSearch}
                onChange={e => setRouteSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ overflowY: 'auto', padding: '8px 12px 16px', flex: 1 }}>
              {filteredRoutes.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--move-muted-fg)', fontSize: 14, marginTop: 24 }}>
                  No hay rutas disponibles
                </p>
              )}
              {filteredRoutes.map(r => (
                <button
                  key={r.route_id}
                  onClick={() => { setSelectedRoute(r); setShowRoutePicker(false); setRouteSearch(''); }}
                  style={{
                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 4px', borderBottom: '1px solid #f5f5f5', textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: 'var(--move-grad-primary)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Map size={18} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--move-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.titulo}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--move-muted-fg)' }}>
                      {formatDistance(r.distancia_m)} · +{r.desnivel_m} m · ★ {Number(r.rating_avg).toFixed(1)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
